import { LOCAL_TIMEZONE } from 'lib_ts/enums/datetime.enums';
import { InstallStep } from '../enums/installation';
import {
  BallType,
  HardwareVersion,
  InstallStepStatus,
  MachineStatus,
  StrikeZoneType,
} from '../enums/machine.enums';
import { PitchListOwner } from '../enums/pitch-list.enums';
import { TrackingDevice } from '../enums/training.enums';
import { Nullable } from './common/i-generics';
import { IMachineSummary } from './i-machine-summary';
import { IMachinePermissions } from './i-permissions';
import { IHotkeyConfig } from './i-remote-controls';
import { IFireEvent, IMSTargetEvent } from './i-session-event';
import { INetworkInfo } from './machine-metadata/i-network-info';
import { IMachineStateCurrent } from './machine-msg/i-machine-state';
import { MongoChildRecord } from './mongo/_base';
import { IPresetTrainingSpec } from './training/i-preset-training-spec';

export interface IFirmwareState {
  /** most recent commit id of machine's firmware */
  last_firmware: string;

  /** the last time last_firmware was changed */
  last_firmware_updated: string;
}

export interface ICalibratedState {
  last_calibrated: boolean;
  last_calibrated_updated: string;
}

export interface IMachineHardwareConfig {
  manufacturer: string;

  wheel_type: string;
  ball_screw_type: string;
  hdpe_type: string;

  wheel_serial_w1: string;
  wheel_serial_w2: string;
  wheel_serial_w3: string;

  position_controller: string;
  motor_controller: string;

  spring_setup: string;
  inserter_guide: string;
  vention_box_location: string;

  ip_address: string;
  mac_address: string;
  computer_model?: Nullable<string>;

  tv_id: string;
}

interface ILastFireDetails {
  _id: string;
  _created: string;
}

interface ITrainingDetails {
  /** minimum number of shots for training pitches */
  training_threshold: number;

  tracking_device: TrackingDevice;

  rapsodo_serial: string;

  precision_train_spec?: IPresetTrainingSpec;

  enable_confidence_checks: boolean;
}

export interface IInstallationDetails {
  // e.g. when starting a RT from the sidebar post-install
  standalone: Nullable<InstallStep>;

  // user email
  started_by: string;
  // user email
  finished_by: Nullable<string>;

  start_date: string;
  end_date: Nullable<string>;

  ball_type: BallType;

  // true once user clicks Start on BTC or RT
  ball_type_locked: boolean;

  // data-collections > _id
  btcID: Nullable<string>;
  // data-collections > _id
  rtID: Nullable<string>;

  // from vision-camera-calibrations > _id
  vccID: Nullable<string>;

  // vision calibration - distance
  vcdFt: Nullable<number>;
  // true once user clicks Complete Calibration during install
  vcdComplete: Nullable<boolean>;

  // if ball type calibration completes, a model will be created and noted here
  model_id: Nullable<string>;

  step_test_shot: Nullable<InstallStepStatus>;
  step_general_config: Nullable<InstallStepStatus>;
  step_hardware_changes: Nullable<InstallStepStatus>;
  step_projector_config: Nullable<InstallStepStatus>;
  step_vision: Nullable<InstallStepStatus>;
  step_ball_type: Nullable<InstallStepStatus>;
  step_repeatability: Nullable<InstallStepStatus>;
  step_completion: Nullable<InstallStepStatus>;
}

interface IMachineAuditDetails {
  // date, shots created earlier than this should not be used for training or modelling
  last_hardware_changed: string;

  last_ball_type_changed: string;

  // whatever [releaseFt - insetFt] was prior to the update
  last_plate_distance_ft: number;
  // whenever plate distance is changed (via releaseFt)
  last_plate_distance_changed: string;
  // the user who made the change
  last_plate_distance_changedBy: string;

  // only set when a user completes a Vision Distance to Plate calibration (i.e. sets releaseFt via vision)
  last_vision_plate_calibration_changed: string;
}

// immutable fields from IFireEvent stored on the machine model for ease of access
export interface IMachineLastFire {
  _id: string;
  _created: string;
  pitch_name?: string;
}

interface IRealTimeApiConfig {
  // if populated, real time API calls will be posted here
  realTimeApiUrl?: Nullable<string>;
  // if populated, real time API calls will include this header key
  realTimeApiHeaderKey?: Nullable<string>;
  // if populated, real time API calls will include this header key-value
  realTimeApiHeaderValue?: Nullable<string>;
}

export interface IMachineFeatureFlags {
  // user-facing language for this feature has shifted from "Jitter Mode" to "Deception Mode", but we'll keep using "jitter_mode" for dev purposes
  jitter_mode?: boolean; // show the Deception Mode input in the Machine Settings dialog
  hawkeye?: boolean; // is the machine allowed to use hawkeye as a tracking device?
  legacy_hit_model?: boolean; // use the legacy model to calculate hit results for this machine
  liveboard?: boolean; // allow users to connect a Liveboard to this machine
}

export interface IMachine
  extends
    MongoChildRecord,
    Partial<IMachineAuditDetails>,
    Partial<IFirmwareState>,
    Partial<ICalibratedState>,
    ILastFireDetails,
    ITrainingDetails,
    IRealTimeApiConfig {
  ignore_game_status: boolean;

  // there can only be one installation in progress at a time
  last_installation?: IInstallationDetails;

  // reported by the machine when it boots
  network_info?: INetworkInfo;

  // IANA timezone string (e.g. America/New_York). Derived from network_info.public_ip.
  timezone?: string;

  // for standalone VCC if last_installation is already complete/cancelled
  last_vccID: Nullable<string>;

  hardware_version: HardwareVersion;

  machineID: string;

  /** hint: A user-friendly name for identifying this machine, such as its location or facility. */
  nickname?: string;

  affiliateTeamPk: Nullable<number>; // from stats API

  /** for distinguishing between real vs mock machines */
  sandbox?: boolean;

  /** most recent domain that this machine was detected connecting to */
  last_domain: string;

  // * equal to plate distance + inset, i.e. distance from wheels of the machine (not the front of the machine) to the back tip of home plate
  releaseFt: number;

  /** key: model_key, value: model_id */
  model_ids?: {
    [key: string]: string;
  };

  // from the most recent hw config backup
  hw_config_id?: string;
  hardware_config?: IMachineHardwareConfig;

  /** ball type of balls currently loaded into the machine */
  ball_type: BallType;

  /** which ball types should be visible/usable by users of this machine, undefined implies MLB */
  ball_type_options: BallType[];

  strikeZoneType: StrikeZoneType;

  suppress_data: boolean;

  notes: string;

  /** if a machine has super access, it will need to access all videos from all teams */
  super: boolean;

  /** controls all of the following:
   * whether to send updates for all tracking devices for tracking updates (real-time API)
   * whether to export data from all tracking devices for session history (in app)
   * whether to include data from all tracking devices for fires (customer API)
   * */
  enable_tracking_debug: boolean;

  /** strike zone */
  enable_pitch_recap: boolean;

  /** hit distance, speed, angle */
  enable_hit_recap: boolean;

  /** hitter name as shown on the machine above the strikezone */
  enable_recap_hitter_name: boolean;

  /** displays details about loaded pitch */
  enable_pitch_preview: boolean;

  /** displays details about active hitter */
  enable_hitter_details: boolean;

  /** if true, changing sections and other movements around the UI will auto-reset the machine's ms */
  enable_auto_reset_ms: boolean;

  /** if true, selecting Trajekt Vision option in tracking device will be allowed */
  enable_trajekt_vision: boolean;

  /** if true and the server is configured accordingly, auto-update model will apply to this machine */
  enable_auto_update_model: boolean;

  /** if true and the server is configured accordingly, auto-rebuild on-demand snapshots will apply to this machine */
  enable_auto_rebuild_snapshots: boolean;

  /** if true, sends 'jitter: true' in mstarget messages to enable random pre-bp fire movements */
  enable_jitter?: boolean;

  // set whenever we touch the machine for checks re: auto-updating the model
  last_auto_update_checked?: string | Date;
  // set whenever we touch the machine for checks re: auto-updating the model
  last_auto_update_outcome?: string;

  default_video_rhp: Nullable<string>;
  default_video_lhp: Nullable<string>;

  permissions?: Partial<IMachinePermissions>;

  isMetric?: boolean;

  remote_hotkeys?: Partial<IHotkeyConfig>;

  feature_flags?: IMachineFeatureFlags;

  last_fire?: ILastFireDetails;

  system_info?: IMachineSystemInfo;

  // record ID from HW config table on Airtable
  at_record_id_hw_config?: string;
}

/** for admin portal display of machines */
export interface IAdminMachine extends IMachine, IMachineSummary {
  isInGame: boolean;
  machine_status?: MachineStatus;
  /** most recent mstarget event sent to the machine */
  lastMS?: IMSTargetEvent;
  lastFire?: IFireEvent;
}

export interface IMachineStateCurrentMsg {
  ms: IMachineStateCurrent;
  received: Date;
}

export interface IMachineSystemComputer {
  type: string;
  serial: string;
}

export interface IMachineSystemCPU {
  type: string;
}

export interface IMachineSystemGPU {
  type: string;
  /** VRAM and driver may be null if Arc model does not have GPU */
  total_memory?: string;
  driver_version?: string;
}

/** Primary SSD / NVMe device details, may be null if Arc model does not have SSD */
export interface IMachineSystemSSD {
  name?: string;
  model?: string;
  vendor?: string;
  serial?: string;
  /** e.g. nvme, sata */
  interface?: string;
  firmware_rev?: string;
  /** device path, e.g. /dev/nvme0 */
  location?: string;
}

export interface IMachineSystemOSInfo {
  ubuntu_version: string;
  kernel_version: string;
}

export interface IMachineSystemInfo {
  computer: IMachineSystemComputer;
  cpu: IMachineSystemCPU;
  gpu: IMachineSystemGPU;
  ssd: IMachineSystemSSD;
  os_info: IMachineSystemOSInfo;
  /** ISO 8601-style timestamp */
  last_boot_time: string;
}

const DEFAULT_TRAINING_DETAILS: ITrainingDetails = {
  training_threshold: 3,

  tracking_device: TrackingDevice.RapsodoV3PRO,

  rapsodo_serial: '',

  enable_confidence_checks: false,
};

const LATEST_HW_VERSION = Object.values(HardwareVersion).slice(-1)[0];

export const DEFAULT_EDITOR_MACHINE: Partial<IMachine> = {
  machineID: '',

  releaseFt: 56.5,
  ball_type: BallType.MLB,

  super: false,
  suppress_data: false,

  _parent_def: PitchListOwner.Team,
  _parent_field: 'machines',

  hardware_version: LATEST_HW_VERSION,

  enable_auto_update_model: true,
  enable_hit_recap: true,
  enable_pitch_recap: true,
  enable_recap_hitter_name: false,
  enable_tracking_debug: false,
  enable_trajekt_vision: false,

  timezone: LOCAL_TIMEZONE,

  ...DEFAULT_TRAINING_DETAILS,
};

export const DEFAULT_CONTEXT_MACHINE: IMachine = {
  _changed: '',
  _created: '',
  _id: '',
  _parent_def: PitchListOwner.Team,
  _parent_field: 'machines',
  _parent_id: '',
  affiliateTeamPk: null,
  ball_type_options: [BallType.MLB],
  ball_type: BallType.MLB,
  enable_auto_reset_ms: false,
  enable_auto_update_model: false,
  enable_auto_rebuild_snapshots: false,
  enable_hit_recap: true,
  enable_hitter_details: false,
  enable_pitch_preview: false,
  enable_pitch_recap: true,
  enable_recap_hitter_name: true,
  enable_tracking_debug: false,
  enable_trajekt_vision: false,
  enable_jitter: false,
  hardware_version: LATEST_HW_VERSION,
  ignore_game_status: false,
  last_domain: 'unknown',
  machineID: '',
  model_ids: {},
  notes: '',
  releaseFt: 56.5,
  strikeZoneType: StrikeZoneType.Default,
  super: false,
  suppress_data: false,
  default_video_rhp: null,
  default_video_lhp: null,
  last_vccID: null,
  ...DEFAULT_TRAINING_DETAILS,
};
